import { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Icon, PopoutMenu } from '@bwoty-web/ui-kit';
import { UI_FILTER } from '@bwoty-web/ui-kit/icons';
import * as PriceOfferListActions from '../../../redux/actions/priceOfferListActions';
import { selectPotentialNumberOfPackages } from '../../../redux/selectors/priceOfferListSelectors';
import DepartureMonthFilterContent from './departureMonthFilterContent';
import DepartureAirportFilterContent from './departureAirportFilterContent';
import PriceFilterContent from './PriceFilterContent';
import DestinationFilterContent from './destinationFilterContent';
import DepartureAirportFilter from './departureAirportFilter';
import DestinationFilter from './destinationFilter';
import DepartureMonthFilter from './departureMonthFilter';
import PriceFilter from './priceFilter';

import './filterPanel.scss';

const FilterPanel = ({ siteId, labels, numberOfVisiblePackages, filterOptions, selectedFilters, filterActions, filterPotentials }) => {
  const { departureMonthOptions, departureAirportOptions, destinationTree, minPrice, maxPrice } = filterOptions;
  const { selectedDepartureMonths, selectedDepartureAirports, selectedMaxPrice, selectedDestinations } = selectedFilters;
  const { departureMonthPotentials, departureAirportPotentials, destinationPotentials } = filterPotentials;
  const {
    addDepartureMonthFilter,
    removeDepartureMonthFilter,
    addDepartureAirportFilter,
    removeDepartureAirportFilter,
    setMaxPriceFilter,
    removeMaxPriceFilter,
    addDestinationFilter,
    removeDestinationFilter,
    clearAllFilters,
  } = filterActions;

  const [filterIsOpen, setFilterIsOpen] = useState(false);

  const handleCloseMenu = () => {
    setFilterIsOpen(false);
  };
  const triggerElement =
    typeof window !== 'undefined'
      ? document.getElementById('filter-menu-toggle')
      : null;

  const filterFooter = (
    <div className="po-list-filter-panel-footer">
      <Button
        className="po-list-filter-clear-button"
        variant="secondary"
        onClick={clearAllFilters}
      >
        {labels.clearFilter}
      </Button>
      <Button
        className="po-list-filter-show-results-button"
        variant="primary"
        onClick={handleCloseMenu}
      >
        {labels.showResults.replace('{numberOfTrips}', numberOfVisiblePackages)}
      </Button>
    </div>
  );

  const countSelectedFilters = () => {
    let count = 0;
    if (selectedDepartureMonths && selectedDepartureMonths.length > 0) count++;
    if (selectedDepartureAirports && selectedDepartureAirports.length > 0) count++;
    if (selectedMaxPrice) count++;
    if (selectedDestinations && selectedDestinations.length > 0) count++;
    return count === 0 ? '' : `(${count})`;
  };

  const selectedFilterCount = countSelectedFilters();

  const departureMonthFilterContent = (
    <DepartureMonthFilterContent
      labels={labels}
      departureMonthOptions={departureMonthOptions}
      selectedDepartureMonths={selectedDepartureMonths}
      addDepartureMonthFilter={addDepartureMonthFilter}
      removeDepartureMonthFilter={removeDepartureMonthFilter}
      departureMonthPotentials={departureMonthPotentials}
    />
  );

  const departureAirportFilterContent = (
    <DepartureAirportFilterContent
      labels={labels}
      departureAirportOptions={departureAirportOptions}
      selectedDepartureAirports={selectedDepartureAirports}
      addDepartureAirportFilter={addDepartureAirportFilter}
      removeDepartureAirportFilter={removeDepartureAirportFilter}
      departureAirportPotentials={departureAirportPotentials}
    />
  );

  const priceFilterContent = (
    <PriceFilterContent
      siteId={siteId}
      labels={labels}
      minPrice={minPrice}
      maxPrice={maxPrice}
      selectedMaxPrice={selectedMaxPrice}
      setSelectedMaxPrice={setMaxPriceFilter}
      removeMaxPriceFilter={removeMaxPriceFilter}
    />
  );

  const destinationFilterContent = (
    <DestinationFilterContent
      labels={labels}
      destinationTree={destinationTree}
      selectedDestinations={selectedDestinations}
      addDestinationFilter={addDestinationFilter}
      removeDestinationFilter={removeDestinationFilter}
      destinationPotentials={destinationPotentials}
    />
  );
  const departureAirportFilterTitle = (
    <DepartureAirportFilter
      labels={labels}
      selectedDepartureAirports={selectedDepartureAirports}
    />
  );
  const destinationFilterTitle = (
    <DestinationFilter
      labels={labels}
      selectedDestinations={selectedDestinations}
    />
  )
  const departureMonthFilterTitle = (
    <DepartureMonthFilter
      labels={labels}
      selectedDepartureMonths={selectedDepartureMonths}
    />
  );
  const priceFilterTitle = (
    <PriceFilter
      labels={labels}
      selectedMaxPrice={selectedMaxPrice}
    />
  );

  const filterGroup = [
    {
      id: 'departureAirportFilter',
      title: departureAirportFilterTitle,
      content: departureAirportFilterContent,
    },
    {
      id: 'destinationFilter',
      title: destinationFilterTitle,
      content: destinationFilterContent,
    },
    {
      id: 'departureMonthFilter',
      title: departureMonthFilterTitle,
      content: departureMonthFilterContent,
    },
    {
      id: 'priceFilter',
      title: priceFilterTitle,
      content: priceFilterContent,
    },
  ];

  return (
    <>
      <div className="po-list-filter-panel">
        <Button
          className="po-list-filter-button"
          id="filter-menu-toggle"
          variant="secondary-dark"
          iconBefore={<Icon path={UI_FILTER} alt="" size="xs" />}
          onClick={() => setFilterIsOpen(!filterIsOpen)}
        >
          {`${labels?.filterTitleMobile} ${selectedFilterCount}`}
        </Button>
        {filterIsOpen && (
          <div className='po-list-filter-wrapper'>
            <PopoutMenu
              show={filterIsOpen}
              onClose={handleCloseMenu}
              title={labels.filterTitleMobile}
              footerContent={filterFooter}
              menuItems={filterGroup}
              triggerElements={[triggerElement]}
            />
          </div>
        )}
      </div>
    </>
  );
};

FilterPanel.propTypes = {
  siteId: PropTypes.number.isRequired,
  labels: PropTypes.shape({
    filterTitleMobile: PropTypes.string.isRequired,
    placeOfDeparture: PropTypes.string.isRequired,
    destination: PropTypes.string.isRequired,
    departureMonth: PropTypes.string.isRequired,
    priceRange: PropTypes.string.isRequired,
    priceUpTo: PropTypes.string.isRequired,
    chosenFilters: PropTypes.string.isRequired,
    showResults: PropTypes.string.isRequired,
    clearFilter: PropTypes.string.isRequired,
    placeOfDeparture: PropTypes.string.isRequired,
    destination: PropTypes.string.isRequired,
    departureMonth: PropTypes.string.isRequired,
    priceRange: PropTypes.string.isRequired,
  }).isRequired,
  numberOfVisiblePackages: PropTypes.number.isRequired,
  filterOptions: PropTypes.shape({
    departureMonthOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    departureAirportOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
    destinationTree: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  }).isRequired,
  selectedFilters: PropTypes.shape({
    selectedDepartureMonths: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedDepartureAirports: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedMaxPrice: PropTypes.number,
    selectedDestinations: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  filterActions: PropTypes.shape({
    addDepartureMonthFilter: PropTypes.func.isRequired,
    removeDepartureMonthFilter: PropTypes.func.isRequired,
    addDepartureAirportFilter: PropTypes.func.isRequired,
    removeDepartureAirportFilter: PropTypes.func.isRequired,
    setMaxPriceFilter: PropTypes.func.isRequired,
    removeMaxPriceFilter: PropTypes.func.isRequired,
    addDestinationFilter: PropTypes.func.isRequired,
    removeDestinationFilter: PropTypes.func.isRequired,
    clearAllFilters: PropTypes.func.isRequired,
  }).isRequired,
  filterPotentials: PropTypes.shape({
    departureMonthPotentials: PropTypes.shape().isRequired,
    departureAirportPotentials: PropTypes.shape().isRequired,
    destinationPotentials: PropTypes.shape().isRequired,
  }).isRequired,
};

const mapDispatchToProps = (dispatch) => {
  return {
    filterActions: {
      addDepartureMonthFilter: bindActionCreators(PriceOfferListActions.addDepartureMonthFilter, dispatch),
      removeDepartureMonthFilter: bindActionCreators(PriceOfferListActions.removeDepartureMonthFilter, dispatch),
      addDepartureAirportFilter: bindActionCreators(PriceOfferListActions.addDepartureAirportFilter, dispatch),
      removeDepartureAirportFilter: bindActionCreators(PriceOfferListActions.removeDepartureAirportFilter, dispatch),
      setMaxPriceFilter: bindActionCreators(PriceOfferListActions.setMaxPriceFilter, dispatch),
      removeMaxPriceFilter: bindActionCreators(PriceOfferListActions.removeMaxPriceFilter, dispatch),
      addDestinationFilter: bindActionCreators(PriceOfferListActions.addDestinationFilter, dispatch),
      removeDestinationFilter: bindActionCreators(PriceOfferListActions.removeDestinationFilter, dispatch),
      clearAllFilters: bindActionCreators(PriceOfferListActions.clearAllFilters, dispatch),
    },
  };
};

const mapStateToProps = (state) => {
  return {
    filterOptions: state.filter.filterOptions,
    selectedFilters: state.filter.selectedFilters,
    filterPotentials: selectPotentialNumberOfPackages(state),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterPanel);
